import { useEffect, useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { Overline } from "./Reveal";
import { getHomepageHero, getNavigation } from "../lib/sanityQueries";
import { peek } from "../lib/sanity";
import { isExternal } from "../lib/siteNav";

// Homepage hero (EP-001 v1.1: D19, D25, D26, D31).
// Left: label, headline, supporting line, the two calls to action and the three principles.
// Right: the promise panel, which explains the brand promise rather than decorating it:
// "Technology should create confidence, not uncertainty." followed by what Secure,
// Structured and Scalable mean for a client. No logo or watermark. The Sanity "Hero Image"
// field is the reserved photo slot: when an image is added there, it appears above the panel.

const PRINCIPLES = ["Security-First", "Business-Aligned", "People-Focused"];

// Each line restates copy already approved on the site (Business Outcomes and Our Approach).
const PROMISE = [
    { word: "Secure", line: "Layered protection that reduces risk and strengthens resilience." },
    { word: "Structured", line: "Documented, standardized processes with clear accountability." },
    { word: "Scalable", line: "A technology foundation built to support your growth." },
];

const PromisePanel = ({ compact = false }) => (
    <div
        data-testid="hero-promise-panel"
        className={`flex h-full flex-col justify-center rounded-card bg-brand-surface ${compact ? "p-6" : "p-8 sm:p-10"}`}
    >
        <p
            className={`font-heading text-brand-text ${
                compact ? "text-xl leading-8" : "text-[26px] leading-[34px] sm:text-[28px] sm:leading-[38px]"
            }`}
        >
            Technology should create confidence, not uncertainty.
        </p>
        <ul className="mt-6 divide-y divide-brand-line border-t border-brand-line">
            {PROMISE.map(({ word, line }, i) => (
                <li key={word} className="grid grid-cols-[2.25rem_1fr] gap-x-3 py-4">
                    <span className="pt-0.5 text-sm font-semibold text-brand-accent" aria-hidden="true">
                        {String(i + 1).padStart(2, "0")}
                    </span>
                    <p>
                        <strong className="block text-lg font-semibold text-brand-text">{word}</strong>
                        <span className="mt-1 block text-[15px] leading-6 text-brand-muted">{line}</span>
                    </p>
                </li>
            ))}
        </ul>
    </div>
);

// Accent the word that contains "business" (e.g. "Businesses."), keeping punctuation plain.
const renderHeadline = (text) => {
    const match = text.match(/(\S*business\w*)/i);
    if (!match) return text;
    const word = match[1].replace(/[.,!?]+$/, "");
    const start = text.indexOf(word);
    return (
        <>
            {text.slice(0, start)}
            <span className="italic text-brand-accent">{word}</span>
            {text.slice(start + word.length)}
        </>
    );
};

const sanityImage = (url, width) =>
    url ? `${url}?w=${width}&fit=max&auto=format&q=80` : null;

export const Hero = () => {
    const [hero, setHero] = useState(() => peek(getHomepageHero));
    const [nav, setNav] = useState(() => peek(getNavigation));

    useEffect(() => {
        let active = true;
        getHomepageHero()
            .then((data) => active && data && setHero(data))
            .catch(() => {});
        getNavigation()
            .then((data) => active && data && setNav(data))
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    const headline = hero?.heroHeadline || "Enterprise Standards for Growing Businesses.";
    const primary = {
        label: hero?.heroPrimaryCta?.label || nav?.primaryCta?.label || "Schedule a Conversation",
        url: hero?.heroPrimaryCta?.url || nav?.primaryCta?.url || "/contact",
    };
    const secondary = {
        label: hero?.heroSecondaryCta?.label || "Explore Our Services",
        url: hero?.heroSecondaryCta?.url || "#services",
    };
    const imageUrl = hero?.heroImage?.url;
    const hasImage = Boolean(imageUrl);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pb-20 pt-32 sm:pb-24 sm:pt-40"
        >

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:items-stretch lg:gap-14">
                <div className="lg:col-span-7 lg:py-4">
                    {hero?.heroEyebrow?.trim() && (
                        <div className="mb-7">
                            <Overline>{hero.heroEyebrow.trim()}</Overline>
                        </div>
                    )}

                    <h1
                        data-testid="hero-heading"
                        className="font-heading text-[40px] leading-[44px] text-brand-text sm:text-5xl sm:leading-[1.1] lg:text-[56px] lg:leading-[60px]"
                    >
                        {renderHeadline(headline)}
                    </h1>

                    {hero?.heroSupportingMessage && (
                        <p className="mt-6 max-w-2xl text-lg leading-[30px] text-brand-muted">
                            {hero.heroSupportingMessage}
                        </p>
                    )}

                    <div className="mt-9 flex flex-wrap items-center gap-3">
                        <a
                            href={primary.url}
                            {...(isExternal(primary.url) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            data-testid="hero-book-cta"
                            className="inline-flex h-11 items-center gap-2 rounded-control bg-brand-accent px-6 text-sm font-semibold text-brand-on-accent transition-colors duration-150 hover:bg-brand-accent-hover"
                        >
                            {primary.label}
                            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                        </a>
                        <a
                            href={secondary.url}
                            {...(isExternal(secondary.url) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            data-testid="hero-services-link"
                            className="inline-flex h-11 items-center rounded-control border-[1.5px] border-brand-control-border px-6 text-sm font-semibold text-brand-text transition-colors duration-150 hover:bg-brand-surface"
                        >
                            {secondary.label}
                        </a>
                    </div>

                    <ul
                        aria-label="Our principles"
                        className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-brand-muted"
                    >
                        {PRINCIPLES.map((p) => (
                            <li key={p} className="inline-flex items-center gap-1.5">
                                <Check className="h-4 w-4 text-brand-accent" aria-hidden="true" />
                                {p}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="lg:col-span-5">
                    {hasImage ? (
                        <figure className="flex h-full flex-col gap-4">
                            <img
                                src={sanityImage(imageUrl, 1000)}
                                srcSet={`${sanityImage(imageUrl, 600)} 600w, ${sanityImage(imageUrl, 1000)} 1000w`}
                                sizes="(min-width: 1024px) 480px, 100vw"
                                alt={hero?.heroImage?.alt || ""}
                                className="aspect-[4/3] w-full rounded-card object-cover"
                                fetchPriority="high"
                            />
                            <PromisePanel compact />
                        </figure>
                    ) : (
                        <PromisePanel />
                    )}
                </div>
            </div>
        </section>
    );
};
